import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { SlicePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import {
  AiPostActionDialogComponent,
  AiPostActionDialogData,
} from '@common/popups/ai-post-action-dialog/ai-post-action-dialog.component';
import { getProviderDisplayIcon, getProviderDisplayName } from '@admin/shared/constants/constant';
import { AiCreatePostModalService } from '@features/posts/services/ai-create-post-modal.service';
import { AiPostDeliveryMode, GeneratedPost } from '@features/posts/models/ai-post.model';
import { minScheduleDateIso } from '@features/posts/utils/ai-post-schedule.utils';

export type ReviewLayoutDensity = 'comfortable' | 'compact' | 'compact-tight';

@Component({
  selector: 'app-step-review',
  standalone: true,
  imports: [SlicePipe, FormsModule, RouterLink],
  templateUrl: './step-review.component.html',
  styleUrl: './step-review.component.scss',
})
export class StepReviewComponent implements AfterViewInit, OnDestroy {
  protected readonly service = inject(AiCreatePostModalService);
  private readonly dialogService = inject(DialogService);
  private readonly reviewRoot = viewChild<ElementRef<HTMLElement>>('reviewRoot');
  private resizeObserver: ResizeObserver | null = null;
  private highlightTimer: ReturnType<typeof setTimeout> | null = null;
  readonly highlightTarget = signal<'connect-accounts' | 'select-accounts' | null>(null);
  readonly layoutDensity = signal<ReviewLayoutDensity>('comfortable');
  posts = this.service.generatedPosts;
  accounts = this.service.connectedAccounts;
  selectedAccountUuids = this.service.selectedAccountUuids;
  isPersisting = this.service.isPersisting;
  persistError = this.service.persistError;
  deliveryMode = this.service.deliveryMode;
  readonly minScheduleDate = minScheduleDateIso();
  readonly previewPost = computed(() => {
    const id = this.service.previewPostId();
    if (!id) return null;
    return this.service.generatedPosts().find((p) => p.id === id) ?? null;
  });

  ngAfterViewInit() {
    this.setupLayoutObserver();
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    if (this.highlightTimer) {
      clearTimeout(this.highlightTimer);
    }
  }

  togglePost(id: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.service.togglePostSelection(id);
  }

  selectAll() {
    this.service.selectAllPosts();
  }

  deselectAll() {
    this.service.deselectAllPosts();
  }

  openPreview(post: GeneratedPost, event?: Event) {
    event?.stopPropagation();
    this.service.openPreview(post.id);
  }

  closePreview() {
    this.service.closePreview();
  }

  getEditablePostText(post: GeneratedPost): string {
    return this.service.getPostBodyPlainText(post);
  }

  onPreviewContentChange(value: string) {
    const id = this.service.previewPostId();
    if (!id) return;
    this.service.updateGeneratedPost(id, {
      caption: value,
      hook: '',
      hashtags: '',
      cta: '',
    });
  }

  async confirmDelivery() {
    if (this.hasPendingSelectedImages) {
      this.openActionDialog({
        title: 'Images still generating',
        message: 'Hang tight — AI is finishing your post visuals.',
        variant: 'images',
        steps: [
          'Wait until each selected post shows its image.',
          'Then save to drafts or schedule your posts.',
        ],
      });
      return;
    }
    if (!this.canConfirm) {
      this.showValidationError();
      return;
    }
    await this.service.confirmDelivery();
  }

  private openActionDialog(
    data: AiPostActionDialogData,
    highlightAfter?: 'connect-accounts' | 'select-accounts'
  ) {
    const ref = this.dialogService.open(AiPostActionDialogComponent, {
      data,
      width: '440px',
      breakpoints: { '575px': '92vw' },
      styleClass: 'ai-post-action-dialog-panel',
      modal: true,
      closable: true,
      dismissableMask: true,
    });
    ref.onClose.subscribe(() => {
      if (highlightAfter) {
        this.flashHighlight(highlightAfter);
      }
    });
  }

  private flashHighlight(target: 'connect-accounts' | 'select-accounts') {
    if (this.highlightTimer) {
      clearTimeout(this.highlightTimer);
    }
    this.highlightTarget.set(target);
    this.highlightTimer = setTimeout(() => {
      this.highlightTarget.set(null);
      this.highlightTimer = null;
    }, 4500);

    setTimeout(() => {
      document.getElementById(`review-${target}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 150);
  }

  private showValidationError() {
    if (this.requiresAccounts && !this.hasConnectedAccounts) {
      this.openActionDialog(
        {
          title: 'No connected accounts',
          message: 'Save as drafts for now, then connect an account when you are ready to publish.',
          variant: 'accounts',
          steps: [
            'Choose Save as drafts at the top to keep your posts.',
            'Connect Facebook, Instagram, or another platform below.',
            'Return here to schedule or queue once an account is connected.',
          ],
          confirmLabel: 'Got it',
        },
        'connect-accounts'
      );
      return;
    }

    if (this.requiresAccounts && !this.hasSelectedAccounts && this.hasConnectedAccounts) {
      this.openActionDialog(
        {
          title: 'Select connected accounts',
          message: 'Choose at least one connected account to publish these posts.',
          variant: 'accounts',
          steps: [
            'Find the Accounts row above your posts.',
            'Click an account avatar to select it.',
          ],
          confirmLabel: 'Select accounts',
        },
        'select-accounts'
      );
      return;
    }

    if (this.showScheduleFields && !this.hasCompleteSchedule) {
      const missing = this.selectedPostsMissingSchedule.length;
      this.openActionDialog({
        title: 'Set publish dates',
        message: `Add a date and time for ${missing} selected post${missing === 1 ? '' : 's'}.`,
        variant: 'schedule',
        steps: [
          'Check the box on each post you want to schedule.',
          'Fill in the Date and Time fields on that post card.',
          'Use Spread across weekdays to auto-fill weekdays.',
        ],
      });
      return;
    }

    if (!this.selectedCount) {
      this.openActionDialog({
        title: 'Select posts first',
        message: 'Choose at least one generated post to continue.',
        variant: 'posts',
        steps: ['Use the checkbox on each post card to include it.'],
      });
      return;
    }

    const reason = this.confirmBlockReason;
    if (reason) {
      this.openActionDialog({
        title: 'One more step',
        message: reason,
        variant: 'generic',
      });
    }
  }

  setDeliveryMode(mode: AiPostDeliveryMode) {
    this.highlightTarget.set(null);
    this.service.setDeliveryMode(mode);
  }

  onScheduleDateChange(postId: string, value: string) {
    const post = this.posts().find((item) => item.id === postId);
    if (!post) return;
    this.service.updatePostSchedule(postId, value, post.scheduledTime || '10:00');
  }

  onScheduleTimeChange(postId: string, value: string) {
    const post = this.posts().find((item) => item.id === postId);
    if (!post) return;
    this.service.updatePostSchedule(postId, post.scheduledDate || this.minScheduleDate, value);
  }

  spreadAcrossWeek() {
    this.service.spreadSelectedSchedulesAcrossWeek();
  }

  get selectedCount(): number {
    return this.posts().filter((post) => post.isSelected).length;
  }

  get confirmLabel(): string {
    const count = this.selectedCount;
    const noun = `${count} post${count === 1 ? '' : 's'}`;
    switch (this.deliveryMode()) {
      case 'draft':
        return `Save ${noun} to drafts`;
      case 'posting_schedule':
        return `Add ${noun} to posting schedule`;
      default:
        return `Schedule ${noun}`;
    }
  }

  get deliveryModeHint(): string {
    switch (this.deliveryMode()) {
      case 'draft':
        return 'No publish dates yet. You can schedule later from Planner.';
      case 'posting_schedule':
        return 'Posts use your workspace posting times. Manage them in Posting times.';
      default:
        return 'Pick a date and time for each post below. Defaults start tomorrow at 10:00 AM.';
    }
  }

  get showScheduleFields(): boolean {
    return this.deliveryMode() === 'schedule_each';
  }

  get requiresAccounts(): boolean {
    return this.deliveryMode() !== 'draft';
  }

  get selectedPosts(): GeneratedPost[] {
    return this.posts().filter((post) => post.isSelected);
  }

  get selectedPostsMissingSchedule(): GeneratedPost[] {
    if (!this.showScheduleFields) {
      return [];
    }
    return this.selectedPosts.filter(
      (post) => !post.scheduledDate?.trim() || !post.scheduledTime?.trim()
    );
  }

  get hasCompleteSchedule(): boolean {
    return this.selectedPostsMissingSchedule.length === 0;
  }

  get canConfirm(): boolean {
    if (!this.selectedCount || this.isPersisting() || this.hasPendingSelectedImages) {
      return false;
    }
    if (this.requiresAccounts && !this.hasSelectedAccounts) {
      return false;
    }
    if (this.showScheduleFields && !this.hasCompleteSchedule) {
      return false;
    }
    return true;
  }

  get isConfirmDisabled(): boolean {
    return this.isPersisting() || this.hasPendingSelectedImages;
  }

  get topAlertMessage(): string {
    if (this.persistError()) {
      return this.persistError();
    }
    return this.confirmBlockReason;
  }

  get hasPendingSelectedImages(): boolean {
    return this.selectedPosts.some(
      (post) =>
        post.hasImage &&
        !post.imageUrl?.trim() &&
        (post.isImageLoading || this.postExpectsGeneratedImage(post))
    );
  }

  private postExpectsGeneratedImage(post: GeneratedPost): boolean {
    return !!(
      post.imageRequestId?.trim() ||
      (Array.isArray(post.imageRequestIds) && post.imageRequestIds.some((id) => id?.trim()))
    );
  }

  get confirmBlockReason(): string {
    if (this.hasPendingSelectedImages) {
      const pending = this.selectedPosts.filter(
        (post) =>
          post.hasImage &&
          !post.imageUrl?.trim() &&
          (post.isImageLoading || this.postExpectsGeneratedImage(post))
      ).length;
      return `Waiting for ${pending} AI image${pending === 1 ? '' : 's'} to finish generating.`;
    }
    if (!this.selectedCount) {
      return 'Select at least one post to continue.';
    }
    if (this.requiresAccounts && !this.hasConnectedAccounts) {
      return 'No connected accounts. Save as drafts, or connect an account to publish.';
    }
    if (this.requiresAccounts && !this.hasSelectedAccounts) {
      return 'Select connected accounts to publish.';
    }
    if (this.showScheduleFields && !this.hasCompleteSchedule) {
      const missing = this.selectedPostsMissingSchedule.length;
      return `Set date and time for ${missing} selected post${missing === 1 ? '' : 's'}.`;
    }
    return '';
  }

  get readinessSteps(): { id: string; label: string; complete: boolean; visible: boolean }[] {
    const steps = [
      {
        id: 'posts',
        label: `Posts (${this.selectedCount}/${this.posts().length})`,
        complete: this.selectedCount > 0,
        visible: true,
      },
      {
        id: 'accounts',
        label: this.hasSelectedAccounts
          ? `Accounts (${this.selectedAccountUuids().length})`
          : 'Accounts',
        complete: !this.requiresAccounts || this.hasSelectedAccounts,
        visible: this.requiresAccounts,
      },
      {
        id: 'schedule',
        label: this.hasCompleteSchedule
          ? 'Dates set'
          : `Dates (${this.selectedPostsMissingSchedule.length} left)`,
        complete: !this.showScheduleFields || this.hasCompleteSchedule,
        visible: this.showScheduleFields,
      },
    ];
    return steps.filter((step) => step.visible);
  }

  isPostScheduleIncomplete(post: GeneratedPost): boolean {
    return (
      this.showScheduleFields &&
      post.isSelected &&
      (!post.scheduledDate?.trim() || !post.scheduledTime?.trim())
    );
  }

  isCurrentReadinessStep(index: number): boolean {
    const steps = this.readinessSteps;
    if (steps[index]?.complete) {
      return false;
    }
    return steps.slice(0, index).every((step) => step.complete);
  }

  get readinessSummary(): string {
    const steps = this.readinessSteps;
    if (!steps.length) {
      return '';
    }

    const currentIndex = steps.findIndex((_, index) => this.isCurrentReadinessStep(index));
    const index = currentIndex >= 0 ? currentIndex : Math.max(steps.length - 1, 0);
    return `Step ${index + 1} of ${steps.length}: ${steps[index].label}`;
  }

  private setupLayoutObserver() {
    const el = this.reviewRoot()?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }

    const updateDensity = (height: number) => {
      const density: ReviewLayoutDensity =
        height < 580 ? 'compact-tight' : height < 740 ? 'compact' : 'comfortable';
      if (this.layoutDensity() !== density) {
        this.layoutDensity.set(density);
      }
    };

    this.resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height ?? 0;
      updateDensity(height);
    });
    this.resizeObserver.observe(el);
    updateDensity(el.getBoundingClientRect().height);
  }

  toggleAccount(uuid: string) {
    this.highlightTarget.set(null);
    this.service.toggleAccountSelection(uuid);
  }

  getPlatformIcon(platform: string): string {
    const map: Record<string, string> = {
      Instagram: 'instagram',
      Facebook: 'facebook',
      'X / Twitter': 'twitter',
      LinkedIn: 'linkedin',
    };
    return map[platform] || platform.toLowerCase().replace(/\s+/g, '_');
  }

  getAccountLabel(account: any): string {
    return account?.name || account?.username || account?.provider || 'Account';
  }

  getAccountAvatar(account: any): string {
    return (
      account?.profile_image_url ||
      account?.profile_picture ||
      account?.profilePicture ||
      account?.avatar ||
      account?.image ||
      account?.picture ||
      ''
    );
  }

  getAccountInitial(account: any): string {
    const label = this.getAccountLabel(account).trim();
    return label ? label.charAt(0).toUpperCase() : '?';
  }

  getProviderIcon(provider: string): string {
    return getProviderDisplayIcon(provider);
  }

  getProviderLabel(provider: string): string {
    return getProviderDisplayName(provider);
  }

  getProviderInitial(provider: string): string {
    const label = this.getProviderLabel(provider);
    return label ? label.charAt(0).toUpperCase() : 'S';
  }

  get processingImageCount(): number {
    return this.posts().filter((post) => post.isImageLoading).length;
  }

  get isImageProcessing(): boolean {
    return this.processingImageCount > 0;
  }

  get hasConnectedAccounts(): boolean {
    return this.accounts().length > 0;
  }

  get hasSelectedAccounts(): boolean {
    return this.selectedAccountUuids().length > 0;
  }
}
